import SignatureService from '@/services/signatureService';
import i18n from '@/plugins/i18n';
import { DocumentStatus } from '@/types/enums/DocumentStatus';
import { SignStatus } from '@/types/enums/SignStatus';
import {
  cloneDeep,
  downloadFile,
  downloadFilesAsZip,
  groupBy
} from '@/composables/useUtils';
import type { Document, DocumentFile } from '@/types/Document';
import type { Signee } from '@/types/Signee';
import type { Observer } from '@/types/Observer';
import type { OverviewDocument } from '@/stores/overview.ts';
import { SignatureType } from '@/types/enums/SignatureType';
import { SigneeType } from '@/types/enums/SigneeType.ts';
import { useMessageStore } from '@/stores/message.ts';
import { useApi } from '@/composables/useApi.ts';
import { getEnvelopeFile } from '@/api/deepsign/documents-files';

export function getCurrentStatusPage(
  userEmail: string,
  document: Document
): string {
  switch (document.documentStatus) {
    case DocumentStatus.DRAFT:
      return `/document/${document.documentId}/create`;
    case DocumentStatus.IN_PROGRESS:
      if (!SignatureService.shouldISign(userEmail, document?.signees ?? [])) {
        return `/document/${document.documentId}/details`;
      }
      if (SignatureService.isSignedByUser(userEmail, document?.signees ?? [])) {
        return `/document/${document.documentId}/details`;
      } else {
        return `/document/${document.documentId}/sign`;
      }
    case DocumentStatus.WITHDRAWN:
      return `/document/${document.documentId}/details`;
    default:
      return `/document/${document.documentId}/details`;
  }
}

export function getCurrentStatusPageFromOverview(document: OverviewDocument) {
  if (!document) return undefined;
  switch (document?.documentStatus) {
    case DocumentStatus.DRAFT:
      return `/document/${document.documentId}/create`;
    case DocumentStatus.IN_PROGRESS:
      if (!SignatureService.shouldISignFromOverview(document)) {
        return `/document/${document.documentId}/details`;
      }
      if (SignatureService.isSignedByUserFromOverview(document)) {
        return `/document/${document.documentId}/details`;
      } else if (document.signatureType === SignatureType.SIGNATURE) {
        return `/document/${document.documentId}/sign`;
      } else if (document.signatureType === SignatureType.SEAL) {
        return `/document/${document.documentId}/seal`;
      } else {
        return `/document/${document.documentId}/details`;
      }
    case DocumentStatus.WITHDRAWN:
      return `/document/${document.documentId}/details`;
    default:
      return `/document/${document.documentId}/details`;
  }
}

// Downloads a document when only its id is known (e.g. from the overview,
// which carries no file data). The full document has to be fetched just to get
// the download URL(s); envelopes hold more than one file and are zipped.
export async function downloadDocumentPdf(
  documentName: string,
  documentStatus: DocumentStatus,
  documentId: string
) {
  if (!documentId) return;

  const document = await fetchDocumentForDownload(documentId);

  if ((document.files?.length ?? 0) > 1) {
    await downloadEnvelopeFilesAsZip(
      documentId,
      documentName ?? document.documentName,
      document.files
    );
    return;
  }

  await downloadFile(document.documentUrl, documentName, documentStatus);
}

// Downloads all files of an envelope as a single zip. File URLs can go stale
// by the time the user downloads, resulting in a 403 from the content delivery
// backend. In that case, a fresh URL is fetched for the affected file and the
// download is retried once before giving up on it.
export async function downloadEnvelopeFilesAsZip(
  documentId: string,
  documentName: string,
  files: DocumentFile[]
) {
  const urls = files.map((file) => file.fileUrl);
  const fileNames = files.map((file) => file.fileName.replace(/\.pdf$/i, ''));
  const zipFileName = documentName?.replace(/\.pdf$/i, '') || 'document-files';

  const refreshStaleUrl = async (index: number) => {
    const fileId = files[index]?.fileId;
    if (!documentId || !fileId) return undefined;
    try {
      const response = await getEnvelopeFile(documentId, fileId);
      return response.data.fileUrl;
    } catch (error) {
      console.error('Failed to refresh expired envelope file URL:', error);
      return undefined;
    }
  };

  await downloadAllDocumentPdfs(urls, fileNames, zipFileName, refreshStaleUrl);
}

// Downloads the currently selected file of a document (relevant for
// envelopes with multiple files), falling back to the document-level file
// when there's no selection or no per-file data available. File URLs can go
// stale by the time the user downloads, so a fresh URL is fetched and
// retried once on a 403, mirroring downloadAllDocumentPdfs.
export async function downloadSelectedDocumentFile(
  document: Document,
  selectedFileId: string | null
) {
  if (!document?.documentId) return;

  const files = document.files ?? [];
  const selectedFile =
    files.find((file) => file.fileId === selectedFileId) ?? files[0];

  if (!selectedFile) {
    await downloadDocumentPdf(
      document.documentName,
      document.documentStatus,
      document.documentId
    );
    return;
  }

  const refreshStaleUrl = async () => {
    try {
      const response = await getEnvelopeFile(
        document.documentId,
        selectedFile.fileId
      );
      return response.data.fileUrl;
    } catch (error) {
      console.error('Failed to refresh expired file URL:', error);
      return undefined;
    }
  };

  await downloadFile(
    selectedFile.fileUrl,
    selectedFile.fileName,
    document.documentStatus,
    refreshStaleUrl
  );
}

/**
 * A freshly minted URL of the single file whose signatures should be validated.
 *
 * Signature validation is always per file, never per envelope: for an envelope
 * this is the file currently selected in the preview (falling back to the first
 * one when nothing is selected yet), and for a single-file document it is the
 * document itself.
 *
 * The URLs carried on the document go stale within minutes, and DeepValidator
 * dereferences them server-side, so the URL is always re-fetched here rather
 * than read off the document already in memory. Errors are left to the caller
 * so the failure can be reported as a validation problem rather than a download
 * one.
 */
export async function getSignatureValidationUrl(
  document: Document | undefined,
  selectedFileId: string | null
): Promise<string | undefined> {
  if (!document?.documentId) return undefined;

  const files = document.files ?? [];
  const selectedFile =
    files.find((file) => file.fileId === selectedFileId) ?? files[0];

  if (selectedFile?.fileId) {
    const response = await getEnvelopeFile(
      document.documentId,
      selectedFile.fileId
    );
    return response.data.fileUrl ?? selectedFile.fileUrl;
  }

  const { deepSignAPI } = useApi();
  const response = await deepSignAPI.value.documents.getById(
    document.documentId
  );
  return (response.data as Document).documentUrl ?? document.documentUrl;
}

export async function downloadAllDocumentPdfs(
  urls: string[],
  fileNames: string[],
  zipFileName?: string,
  refreshStaleUrl?: (index: number) => Promise<string | undefined>
) {
  try {
    await downloadFilesAsZip(urls, fileNames, zipFileName, refreshStaleUrl);
  } catch (error) {
    console.error('There was an error downloading all the documents');
  }
}

export function getStatusColor(document: OverviewDocument): string {
  const colors = {
    toSign: 'blue-darken-3',
    toApprove: 'indigo',
    toSeal: 'cyan-darken-3',
    pending: 'orange-darken-4',
    declined: 'grey-darken-2',
    completed: 'green-darken-2',
    draft: 'deep-purple-darken-2',
    withdrawn: 'grey-darken-2',
    rejected: 'grey-darken-2'
  };

  if (document.documentStatus === DocumentStatus.SIGNED) {
    return colors.completed;
  }

  if (document.documentStatus === DocumentStatus.DRAFT) {
    return colors.draft;
  }

  if (document.documentStatus === DocumentStatus.WITHDRAWN) {
    return colors.withdrawn;
  }

  if (document.documentStatus === DocumentStatus.REJECTED) {
    return colors.rejected;
  }

  if (document.documentStatus === DocumentStatus.IN_PROGRESS) {
    if (document.signStatus === SignStatus.PENDING) {
      if (document.signatureType === SignatureType.SIGNATURE) {
        if (document.signeeType === SigneeType.APPROVE) {
          return colors.toApprove;
        }
        return colors.toSign;
      } else {
        return colors.toSeal;
      }
    } else return colors.pending;
  }

  return '';
}

export function getStatusText(document: OverviewDocument) {
  const texts = {
    toSign: i18n.global.t('document_status.to_sign'),
    toSeal: i18n.global.t('document_status.to_seal'),
    toApprove: i18n.global.t('document_status.to_approve'),
    pending: i18n.global.t('document_status.pending'),
    declined: i18n.global.t('document_status.declined'),
    completed: i18n.global.t('document_status.completed'),
    draft: i18n.global.t('document_status.draft'),
    withdrawn: i18n.global.t('document_status.withdrawn'),
    rejected: i18n.global.t('document_status.rejected')
  };

  if (document.documentStatus === DocumentStatus.SIGNED) {
    return texts.completed;
  }

  if (document.documentStatus === DocumentStatus.DRAFT) {
    return texts.draft;
  }

  if (document.documentStatus === DocumentStatus.WITHDRAWN) {
    return texts.withdrawn;
  }

  if (document.documentStatus === DocumentStatus.REJECTED) {
    return texts.rejected;
  }

  if (document.documentStatus === DocumentStatus.IN_PROGRESS) {
    if (document.signStatus === SignStatus.PENDING) {
      if (document.signatureType === SignatureType.SIGNATURE) {
        if (document.signeeType === SigneeType.APPROVE) {
          return texts.toApprove;
        }
        return texts.toSign;
      } else {
        return texts.toSeal;
      }
    }
    return texts.pending;
  }

  return '';
}

// Envelope names (fileCount > 1) are user-defined, so they are displayed
// verbatim — a ".pdf" suffix stays because the user chose it. Single-file
// documents keep the original file name and always show the ".pdf" extension.
export function getDisplayDocumentName(document: OverviewDocument): string {
  if (document.fileCount === 1 && !document.documentName.endsWith('.pdf')) {
    return `${document.documentName}.pdf`;
  }
  return document.documentName;
}

export function getUpdateOrderRequestFromSignGroups(signGroups: Signee[][]) {
  console.error(signGroups);
  const requestBody: string[][] = [];
  signGroups.forEach((group) => {
    const signeeIds: string[] = [];
    group.forEach((signee: Signee) => {
      signeeIds.push(signee.signeeId);
    });
    requestBody.push(signeeIds);
  });
  return requestBody.length === 1 ? requestBody[0] : requestBody;
}

export function getSignGroupsFromSignees(signeesList: Signee[]) {
  const signeeGroups: Signee[][] = [];
  let signees: Signee[] = cloneDeep(signeesList);
  let groupIndex = 0;
  signees = signees.sort((a, b) => {
    return a.signOrder - b.signOrder;
  });

  const groupedSignees = groupBy(signees, 'signOrder');
  for (const value of Object.values(groupedSignees)) {
    signeeGroups.push([]);
    value.forEach((signee) => {
      signeeGroups[groupIndex].push(signee);
    });
    groupIndex++;
  }
  return signeeGroups;
}

export function getRequestBodyFromSignGroups(signGroups: Signee[][]) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const requestBody: any = [];
  if (!signGroups) {
    return requestBody;
  }
  if (signGroups.length === 1) {
    signGroups[0].forEach((signee) => {
      requestBody.push([signee.signeeId]);
    });
    return requestBody;
  } else {
    signGroups.forEach((group) => {
      const signeeIds: string[] = [];
      group.forEach((signee) => {
        signeeIds.push(signee.signeeId);
      });
      requestBody.push(signeeIds);
    });
    return requestBody;
  }
}

export function getRequestBodyFromSignees(signeesList: Signee[]) {
  return getRequestBodyFromSignGroups(getSignGroupsFromSignees(signeesList));
}

export function isOrdered(document: Document) {
  return document?.signeesOrdered?.length > 1;
}

export function hasExpertSettingsDefined(document: Document) {
  return hasDocumentObservers(document) || document.signeeAttachmentsAllowed;
}

export function hasDocumentObservers(document: Document) {
  return document?.observers?.length > 0 || false;
}

export function observersToDelete(
  documentObservers: Observer[],
  formObservers: Observer[]
) {
  const toDelete: string[] = [];
  documentObservers.forEach((obs) => {
    const index = formObservers.findIndex(
      (formObs) => formObs.observerId === obs.observerId
    );
    if (index === -1) {
      toDelete.push(obs.observerId);
    }
  });
  return toDelete;
}

export function observersToUpdate(
  documentObservers: Observer[],
  formObservers: Observer[]
) {
  const toUpdate: string[] = [];
  formObservers.forEach((formObs) => {
    const docObs = documentObservers.find(
      (docObs) => docObs.observerId === formObs.observerId
    );
    if (
      (docObs?.observerId && docObs?.email !== formObs?.email) ||
      (docObs?.observerId && docObs?.isAdmin !== formObs?.isAdmin)
    ) {
      toUpdate.push(formObs.observerId);
    }
  });
  return toUpdate;
}

export async function fetchDocumentForDownload(
  documentId: string
): Promise<Document> {
  const { deepSignAPI } = useApi();
  try {
    const response = await deepSignAPI.value.documents.getById(documentId);
    return response.data as Document;
  } catch (error) {
    const messageStore = useMessageStore();
    messageStore.showMessage({
      key: 'errors.api.download_document',
      color: 'error',
      icon: 'fa fa-circle-exclamation'
    });
    return Promise.reject(error);
  }
}

// A single downloadable file resulting from flattening a batch of documents.
// Envelope documents (files.length > 1) contribute one entry per file so none
// of their pdfs get dropped; single-file documents contribute one entry for
// their documentUrl. fileId/documentId are kept so a stale presigned URL can
// be refreshed per entry.
export interface BatchDownloadFile {
  documentId: string;
  fileId?: string;
  url: string;
  fileName: string;
}

export async function fetchBatchDocumentDownloadFiles(
  documentIds: string[]
): Promise<BatchDownloadFile[]> {
  const { deepSignAPI } = useApi();
  try {
    const filesByDocument = await Promise.all(
      documentIds.map(async (id) => {
        const response = await deepSignAPI.value.documents.getById(id);
        const document = response.data as Document;
        const documentName =
          document.documentName?.replace(/\.pdf$/i, '') || document.documentId;

        if ((document.files?.length ?? 0) > 1) {
          return document.files.map((file) => ({
            documentId: document.documentId,
            fileId: file.fileId,
            url: file.fileUrl,
            fileName: `${documentName}_${file.fileName.replace(/\.pdf$/i, '')}`
          }));
        }

        return [
          {
            documentId: document.documentId,
            url: document.documentUrl,
            fileName: documentName
          }
        ];
      })
    );

    return filesByDocument.flat();
  } catch (error) {
    const messageStore = useMessageStore();
    messageStore.showMessage({
      key: 'errors.api.download_document',
      color: 'error',
      icon: 'fa fa-circle-exclamation'
    });
    return Promise.reject(error);
  }
}
